import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '@/i18n';
import {
  extractTranscript,
  getSpeechRecognitionConstructor,
  getSpeechRecognitionLang,
  mapSpeechError,
  type SpeechRecognitionConstructor,
  type SpeechRecognitionMode,
  type SttErrorType,
  type SttUiState,
} from '@/utils/speechRecognition';

export interface UseSpeechToTextOptions {
  value: string;
  onChange: (value: string) => void;
  lang?: string;
  enabled?: boolean;
  /**
   * `oneshot` — stop after a browser-detected pause (default Web Speech behavior).
   * `manual` — keep listening until the user clicks stop.
   * @default 'manual'
   */
  mode?: SpeechRecognitionMode;
}

export interface UseSpeechToTextResult {
  speechSupported: boolean;
  isRecording: boolean;
  sttUiState: SttUiState;
  sttError: SttErrorType | null;
  toggle: () => void;
  stop: () => void;
  clearError: () => void;
}

export function useSpeechToText({
  value,
  onChange,
  lang,
  enabled = true,
  mode = 'manual',
}: UseSpeechToTextOptions): UseSpeechToTextResult {
  const [speechSupported, setSpeechSupported] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [sttUiState, setSttUiState] = useState<SttUiState>('idle');
  const [sttError, setSttError] = useState<SttErrorType | null>(null);

  const speechRecognitionRef = useRef<InstanceType<SpeechRecognitionConstructor> | null>(null);
  const voiceBaseInputValueRef = useRef('');
  const sttHadTranscriptRef = useRef(false);
  const sttUserStoppedRef = useRef(false);
  const sttErrorRef = useRef<SttErrorType | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  sttErrorRef.current = sttError;

  valueRef.current = value;
  onChangeRef.current = onChange;

  const resolveLang = useCallback(
    () => getSpeechRecognitionLang(lang ?? i18n.language ?? 'en'),
    [lang],
  );

  const clearError = useCallback(() => {
    setSttError(null);
    sttErrorRef.current = null;
    setSttUiState((prev) => (prev === 'error' ? 'idle' : prev));
  }, []);

  const stop = useCallback(() => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;
    sttUserStoppedRef.current = true;
    try {
      recognition.stop();
    } catch {
      // ignore
    }
  }, []);

  const toggle = useCallback(() => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;
    setSttError(null);
    sttErrorRef.current = null;

    if (isRecording) {
      sttUserStoppedRef.current = true;
      try {
        recognition.stop();
      } catch (e) {
        console.error('Failed to stop speech recognition', e);
      }
      return;
    }

    sttUserStoppedRef.current = false;
    voiceBaseInputValueRef.current = valueRef.current;
    try {
      recognition.start();
    } catch (e) {
      console.error('Failed to start speech recognition', e);
      setIsRecording(false);
      setSttUiState('idle');
    }
  }, [isRecording]);

  useEffect(() => {
    if (!enabled) {
      setSpeechSupported(false);
      return;
    }

    const Ctor = getSpeechRecognitionConstructor();
    if (!Ctor) {
      setSpeechSupported(false);
      return;
    }

    setSpeechSupported(true);
    const recognition = new Ctor();
    recognition.continuous = mode === 'manual';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = resolveLang();

    recognition.onstart = () => {
      setIsRecording(true);
      setSttUiState('listening');
      setSttError(null);
      sttErrorRef.current = null;
      sttHadTranscriptRef.current = false;
    };

    recognition.onend = () => {
      if (
        mode === 'manual' &&
        !sttUserStoppedRef.current &&
        sttErrorRef.current !== 'permission_denied'
      ) {
        sttErrorRef.current = null;
        setSttError(null);
        voiceBaseInputValueRef.current = valueRef.current;
        try {
          recognition.start();
          return;
        } catch {
          // fall through to idle
        }
      }

      setIsRecording(false);

      if (!sttHadTranscriptRef.current && !sttErrorRef.current && !sttUserStoppedRef.current) {
        setSttError('no_speech');
        sttErrorRef.current = 'no_speech';
        setSttUiState('error');
        return;
      }

      setSttUiState('idle');
    };

    recognition.onerror = (ev: unknown) => {
      const errorCode = (ev as { error?: string })?.error;
      if (errorCode === 'aborted') {
        setIsRecording(false);
        setSttUiState('idle');
        return;
      }

      // In manual mode, ignore no-speech so onend can restart the session.
      if (errorCode === 'no-speech' && mode === 'manual') {
        return;
      }

      const mapped = mapSpeechError(errorCode);
      if (mapped) {
        setSttError(mapped);
        sttErrorRef.current = mapped;
        setSttUiState('error');
      }
      setIsRecording(false);
    };

    recognition.onresult = (ev: unknown) => {
      const clean = extractTranscript(ev);
      if (!clean) return;

      sttHadTranscriptRef.current = true;
      const prefix = voiceBaseInputValueRef.current.trim();
      onChangeRef.current(prefix ? `${prefix} ${clean}` : clean);
    };

    speechRecognitionRef.current = recognition;

    return () => {
      sttUserStoppedRef.current = true;
      try {
        recognition.stop();
      } catch {
        // ignore
      }
      speechRecognitionRef.current = null;
    };
  }, [enabled, mode, resolveLang]);

  useEffect(() => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;
    recognition.lang = resolveLang();
  }, [resolveLang]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      const recognition = speechRecognitionRef.current;
      if (recognition) {
        recognition.lang = getSpeechRecognitionLang(i18n.language ?? 'en');
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  return {
    speechSupported,
    isRecording,
    sttUiState,
    sttError,
    toggle,
    stop,
    clearError,
  };
}
